import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

import { cx } from "@/lib/cx";

/** Full width unless the caller sets an explicit width (Tailwind can't resolve conflicting widths by order). */
const width = (className?: string) => (className && /(^|\s)(w-|max-w-|flex-)/.test(className) ? "" : "w-full");

const control =
  "rounded-md border bg-surface px-2.5 text-base text-fg placeholder:text-fg-subtle shadow-sm " +
  "transition-colors transition-fast focus-visible:border-accent-indicator disabled:bg-surface-sunken disabled:text-fg-muted " +
  "aria-invalid:border-danger";

type FieldProps = {
  label: ReactNode;
  children: ReactElement<{ id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean; required?: boolean }>;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  optional?: boolean;
  className?: string;
};

/** Label + control + hint + error with correct id/aria wiring. */
export function Field({ label, children, hint, error, required, optional, className }: FieldProps) {
  const id = useId();
  const controlId = children.props.id ?? id;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint && !error ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  const child = isValidElement(children)
    ? cloneElement(children, {
        id: controlId,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
        required: required ?? children.props.required,
      })
    : children;
  return (
    <div className={cx("flex flex-col gap-1", className)}>
      {/* The id lets a composite control name its popup after the label (Combobox's listbox). */}
      <label id={`${controlId}-label`} htmlFor={controlId} className="text-sm font-medium text-fg">
        {label}
        {/* A real space (not a margin), so the accessible name reads "Phone (optional)". */}
        {optional && <span className="font-normal text-fg-subtle"> (optional)</span>}
      </label>
      {child}
      {hint && !error && (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs font-medium text-danger-text">
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(control, width(className), "h-8 border-control", className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, rows = 3, ...rest },
  ref,
) {
  return <textarea ref={ref} rows={rows} className={cx(control, width(className), "min-h-16 border-control py-1.5", className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cx(control, width(className), "h-8 border-control pr-7", className)} {...rest}>
      {children}
    </select>
  );
});

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: ReactNode; description?: ReactNode; error?: string };

export function Checkbox({ label, description, error, className, id, ...rest }: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedBy = [description ? `${inputId}-desc` : null, error ? `${inputId}-error` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cx("flex items-start gap-2.5", className)}>
      <input
        id={inputId}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded-xs border-control accent-[var(--accent)] disabled:cursor-not-allowed"
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      <div className="min-w-0">
        <label htmlFor={inputId} className="cursor-pointer text-sm font-medium text-fg">
          {label}
        </label>
        {description && (
          <p id={`${inputId}-desc`} className="text-xs text-fg-subtle">
            {description}
          </p>
        )}
        {error && (
          <p id={`${inputId}-error`} className="text-xs font-medium text-danger-text">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/** A group of radio options styled as a compact segmented control (native radios, keyboard-friendly). */
export function Segmented<T extends string>({
  legend,
  value,
  options,
  onChange,
  name,
  hideLegend,
  error,
}: {
  legend: string;
  value: T;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (value: T) => void;
  name?: string;
  hideLegend?: boolean;
  /** Shown under the options and announced with them. */
  error?: string;
}) {
  const autoName = useId();
  const errorId = `${autoName}-error`;
  return (
    <fieldset className="min-w-0" aria-describedby={error ? errorId : undefined}>
      <legend className={cx("mb-1 text-sm font-medium text-fg", hideLegend && "sr-only")}>{legend}</legend>
      <div className="inline-flex rounded-md border border-control bg-surface-sunken p-0.5">
        {options.map((option) => (
          <label
            key={option.value}
            className={cx(
              "relative cursor-pointer rounded-sm px-2.5 py-1 text-sm transition-colors transition-fast has-[:focus-visible]:shadow-[var(--focus-ring)]",
              value === option.value ? "bg-surface font-medium text-fg shadow-sm" : "text-fg-muted hover:text-fg",
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={name ?? autoName}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              aria-describedby={error ? errorId : undefined}
            />
            {option.label}
          </label>
        ))}
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-xs font-medium text-danger-text">
          {error}
        </p>
      )}
    </fieldset>
  );
}
